import { useEffect } from "react";
import {
  Form,
  useActionData,
  useNavigation,
  useLocation,
  useSearchParams,
  Link,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PasswordInput } from "../../components/common/PasswordInput";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { LanguageSwitcher } from "../../components/common/LanguageSwitcher";
import type { ActionResult } from "../../routes/actions";
import "./auth.css";

export default function LoginPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const { refetchUser } = useAuth();
  const actionData = useActionData() as ActionResult | undefined;
  const rawReturnUrl =
    searchParams.get("returnUrl") ||
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    "/dashboard";
  // Prevent open-redirect: allow relative paths and same-origin absolute URLs
  const sanitizeReturnUrl = (url: string): string => {
    // Relative path starting with /
    if (/^\/[^/\\]/.test(url)) return url;
    // Same-origin absolute URL — extract pathname
    try {
      const parsed = new URL(url);
      if (parsed.origin === window.location.origin)
        return parsed.pathname + parsed.search + parsed.hash;
    } catch {
      // not a valid URL
    }
    return "/dashboard";
  };
  const from = sanitizeReturnUrl(rawReturnUrl);
  const isSubmitting = navigation.state === "submitting";

  useEffect(() => {
    if (searchParams.get("verified") === "true") {
      toast.success(
        t("auth.login.accountVerified", "Account created and verified! Please log in."),
      );
    }
  }, [searchParams, t]);

  useEffect(() => {
    if (!actionData) return;

    if (actionData.success) {
      toast.success(t("auth.login.success"));
      refetchUser().then(() => {
        window.location.href = from;
      });
    } else if (actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData, refetchUser, from, t]);

  return (
    <div className="auth-container">
      <div className="auth-language-switcher">
        <LanguageSwitcher variant="standalone" />
      </div>
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t("auth.login.title")}</h1>
          <p>{t("auth.login.subtitle")}</p>
        </div>

        <Form method="post" className="auth-form">
          {actionData && !actionData.success && actionData.error && (
            <div className="auth-error-banner" role="alert">
              {actionData.error}
            </div>
          )}

          <div className={`form-group ${actionData?.fieldErrors?.email ? "error" : ""}`}>
            <label htmlFor="email">{t("auth.login.emailLabel")}</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder={t("auth.login.emailPlaceholder")}
              autoComplete="email"
            />
            {actionData?.fieldErrors?.email && (
              <span className="form-error">{actionData.fieldErrors.email}</span>
            )}
          </div>

          <div className={`form-group ${actionData?.fieldErrors?.password ? "error" : ""}`}>
            <label htmlFor="password">{t("auth.login.passwordLabel")}</label>
            <PasswordInput
              id="password"
              name="password"
              placeholder={t("auth.login.passwordPlaceholder")}
              autoComplete="current-password"
            />
            {actionData?.fieldErrors?.password && (
              <span className="form-error">{actionData.fieldErrors.password}</span>
            )}
          </div>

          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="loading-spinner"></span> {t("auth.login.submitting")}
              </>
            ) : (
              t("auth.login.submitButton")
            )}
          </button>
        </Form>

        <div className="auth-links">
          <Link to="/forgot-password" className="auth-link">
            {t("auth.login.forgotPassword")}
          </Link>
          <div className="auth-divider">{t("common.or")}</div>
          <Link
            to={
              from !== "/dashboard"
                ? `/register?returnUrl=${encodeURIComponent(from)}`
                : "/register"
            }
            className="auth-link"
          >
            {t("auth.login.noAccount")}
          </Link>
        </div>
      </div>
    </div>
  );
}
